<template>
  <div class="home-header w1200">
    <el-menu
      mode="horizontal"
      background-color="#ffffff"
      text-color="#303133"
      active-text-color="#409eff"
      router
      :ellipsis="false"
      :default-active="activeIndex"
    >
      <el-menu-item index="/home">
        <template #title>
          <span>首页</span>
        </template>
      </el-menu-item>
      <el-menu-item index="/access">
        <template #title>
          <span>登录信息</span>
        </template>
      </el-menu-item>
      <el-menu-item index="/query-history">
        <template #title>
          <span>查询历史</span>
        </template>
      </el-menu-item>
            <el-menu-item index="/batch-query">
        <template #title>
          <span>批量查询</span>
        </template>
      </el-menu-item>
      <el-menu-item index="/cost-query">
        <template #title>
          <span>成本查询</span>
        </template>
      </el-menu-item>

    </el-menu>
    <div class="right-content">
      <el-popover
            placement="bottom"
            :width="300"
            trigger="hover"
            @show="handlePopoverShow"
            @hide="handlePopoverHide"
          >
            <template #reference>
              <span><small>登录信息状态</small></span>
            </template>
            <div class="access-status-popover">
              <div v-if="loading" class="loading">加载中...</div>
              <div v-else-if="error" class="error">{{ error }}</div>
              <div v-else class="access-list">
                <div v-for="item in accessData" :key="item.company_id" class="access-item">
                  <div class="company-info">
                    <div class="company-name">{{ item.company_name }}</div>
                    <div class="refresh-time">刷新时间：{{ formatDate(item.access.refresh_time) }}</div>
                  </div>
                  <div class="status" :class="item.access.is_valid ? 'active' : 'inactive'">
                    {{ item.access.is_valid ? '有效' : '失效' }}
                  </div>
                </div>
              </div>
            </div>
          </el-popover>
      <el-dropdown trigger="hover">
        <el-button link size="large">
          你好，<strong>{{ nickname || '用户' }}</strong>
          <el-icon class="el-icon--right"><ArrowDown /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item @click="handleChangePassword">
              <el-icon><Edit /></el-icon>
              <span>修改密码</span>
            </el-dropdown-item>
            <el-dropdown-item @click="handleLogout">
              <el-icon><SwitchButton /></el-icon>
              <span>退出登录</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    
    <!-- 密码修改对话框 -->
    <el-dialog
      title="修改密码"
      v-model="showChangePassword"
      width="500px"
      @close="handleCancel"
    >
      <el-form
        :model="form"
        :rules="rules"
        ref="formRef"
        label-position="top"
      >
        <el-form-item label="旧密码" prop="oldPassword">
          <el-input
            v-model="form.oldPassword"
            type="password"
            placeholder="请输入旧密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="form.newPassword"
            type="password"
            placeholder="请输入新密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
            v-model="form.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="handleCancel">取消</el-button>
          <el-button type="primary" @click="submitChangePassword" :loading="isSubmitting">
            {{ isSubmitting ? '修改中...' : '修改密码' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { useWuliu178AuthStore } from "@store/auth.js";
import { router } from "@router/router.js";
import { useRoute } from 'vue-router';
import { ref, onMounted, computed, reactive } from 'vue';
import { ArrowDown, Edit, SwitchButton } from '@element-plus/icons-vue';
import { postWlApi } from '@utils/api_v1.js';
import { ElMessage } from 'element-plus';

const route = useRoute();

// 访问状态数据
const accessData = ref([]);
const loading = ref(false);
const error = ref('');

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// 获取访问状态数据
const fetchAccessData = async () => {
  try {
    loading.value = true;
    error.value = '';
    const response = await postWlApi('/company_access');
    
    if (response.code === 0 && response.data?.company_access) {
      // 转换后端返回的嵌套数据结构为前端模板期望的扁平结构
      accessData.value = response.data.company_access.map(item => {
        return {
          company_id: item.company.company_id,
          company_name: item.company.company_name,
          access: item.access 
        };
      });
    } else {
      error.value = '获取数据失败: 无效的响应格式';
    }
  } catch (err) {
    console.error('请求失败:', err);
    error.value = '网络错误，请稍后重试';
  } finally {
    loading.value = false;
  }
};

// Popover显示时触发
const handlePopoverShow = () => {
  fetchAccessData();
};

// Popover隐藏时触发
const handlePopoverHide = () => {
  accessData.value = [];
  error.value = '';
};

// 计算当前激活的菜单项
const activeIndex = computed(() => {
  // 如果是根路径，默认高亮首页
  if (route.path === '/') {
    return '/home';
  }
  return route.path;
});

const nickname = ref('');

onMounted(async () => {
  try {
    const res = await postWlApi('/user/profile');
    if (res && res.data.nickname) {
      nickname.value = res.data.nickname;
    }
  } catch (error) {
    console.error('获取用户信息失败', error);
  }
});

// 密码修改相关功能
const showChangePassword = ref(false);
const form = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
});

const formRef = ref(null);
const isSubmitting = ref(false);

// 表单验证规则
const rules = {
  oldPassword: [
    { required: true, message: '请输入旧密码', trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value && form.oldPassword && value === form.oldPassword) {
          callback(new Error('新密码不能与旧密码相同'));
        } else {
          callback();
        }
      },
      trigger: 'blur'
    }
  ],
  confirmPassword: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== form.newPassword) {
          callback(new Error('两次输入的密码不一致'));
        } else {
          callback();
        }
      },
      trigger: 'blur'
    }
  ]
};

const handleChangePassword = () => {
  // 显示密码修改对话框
  showChangePassword.value = true;
};

const submitChangePassword = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      isSubmitting.value = true;
      
      try {
        const response = await postWlApi('/user/change-password', {
          old_password: form.oldPassword,
          new_password: form.newPassword
        });

        if (response.code === 0) {
          // 清空表单
          form.oldPassword = '';
          form.newPassword = '';
          form.confirmPassword = '';
          // 关闭对话框
          showChangePassword.value = false;
          // 调用密码修改成功处理函数
          handlePasswordChangeSuccess();
        } else {
          ElMessage.error(response.message || '修改密码失败');
        }
      } catch (error) {
        ElMessage.error(error.response?.message || '网络错误，请稍后重试');
      } finally {
        isSubmitting.value = false;
      }
    } else {
      return false;
    }
  });
};

const handleCancel = () => {
  // 清空表单
  form.oldPassword = '';
  form.newPassword = '';
  form.confirmPassword = '';
  // 关闭对话框
  showChangePassword.value = false;
};

const handlePasswordChangeCancel = () => {
  showChangePassword.value = false;
};

const handlePasswordChangeSuccess = () => {
  ElMessage.success({
    message: '密码修改成功',
    duration: 2000,
    offset: 20
  });
};

const handleLogout = () => {
  const wl_auth = useWuliu178AuthStore();
  // 退出登录逻辑
  wl_auth.logout();
  router.push('/login');
};
</script>

<style scoped>
.home-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.right-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.el-dropdown-link {
  cursor: pointer;
  color: #606266;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.el-dropdown-link:hover {
  background-color: #f5f7fa;
}



/* 密码修改对话框样式 */
.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 20px;
}

/* 访问状态popover样式 */
.access-status-popover {
  padding: 10px;
}

.access-status-popover .loading {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}

.access-status-popover .error {
  text-align: center;
  color: #f56c6c;
  padding: 20px 0;
}

.access-status-popover .access-list {
  max-height: 300px;
  overflow-y: auto;
}

.access-status-popover .access-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
}

.access-status-popover .access-item:last-child {
  border-bottom: none;
}

.access-status-popover .company-name {
  flex: 1;
  font-weight: 500;
  color: #303133;
}

.access-status-popover .status {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-right: 10px;
}

.access-status-popover .status.active {
  background-color: #f0f9eb;
  color: #67c23a;
}

.access-status-popover .status.inactive {
  background-color: #fef0f0;
  color: #f56c6c;
}

.access-status-popover .refresh-time {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}
</style>